import { useEffect, useRef, useCallback, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Custom hook for real-time session monitoring.
 * Uses BOTH WebSocket AND HTTP polling for maximum reliability.
 * HTTP polling is the primary method (always active when logged in).
 * WebSocket is supplementary for faster detection when available.
 */
export const useSessionMonitor = (onSessionInvalidated) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const isInvalidatedRef = useRef(false);
  
  const [isConnected, setIsConnected] = useState(false);

  const getToken = useCallback(() => {
    // Try customer token first, then admin token
    return localStorage.getItem('livestreamToken') || localStorage.getItem('adminToken');
  }, []);

  const handleSessionInvalidated = useCallback((message) => {
    // Prevent multiple invalidation calls
    if (isInvalidatedRef.current) return;
    isInvalidatedRef.current = true;
    
    console.log('Session invalidated by another login:', message);
    
    // Clear all auth data
    localStorage.removeItem('livestreamToken');
    localStorage.removeItem('livestreamCustomer');
    localStorage.removeItem('adminToken');
    
    // Stop all monitoring
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    // Call the callback if provided
    if (onSessionInvalidated) {
      onSessionInvalidated(message);
    } else {
      // Default behavior: show alert and redirect
      alert(message || 'Your session has been terminated because someone logged in with your credentials on another device.');
      
      // Redirect to appropriate login page
      const currentPath = window.location.pathname;
      if (currentPath.includes('/admin')) {
        window.location.href = '/admin';
      } else {
        window.location.href = '/live/login';
      }
    }
  }, [onSessionInvalidated]);

  // HTTP polling - PRIMARY method (always runs when token exists)
  const startPolling = useCallback(() => {
    const token = getToken();
    if (!token) {
      console.log('SessionMonitor: No token, skipping polling');
      return;
    }
    
    // Don't start if already polling
    if (pollingIntervalRef.current) {
      console.log('SessionMonitor: Polling already active');
      return;
    }
    
    console.log('SessionMonitor: Starting HTTP polling (primary method)');
    
    const checkSession = async () => {
      // Skip if already invalidated
      if (isInvalidatedRef.current) return;
      
      const currentToken = getToken();
      if (!currentToken) {
        console.log('SessionMonitor: Token removed, stopping polling');
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/session/validate`, {
          headers: {
            'Authorization': `Bearer ${currentToken}`
          }
        });
        
        const data = await response.json();
        
        if (!data.valid && data.reason === 'session_invalidated') {
          // Session was invalidated by another login
          console.log('SessionMonitor: Session invalidated detected via polling');
          handleSessionInvalidated(data.message || 'Your session has been terminated because someone logged in with your credentials on another device.');
        }
      } catch (error) {
        console.error('SessionMonitor: Poll error:', error);
      }
    };
    
    // Check immediately
    checkSession();
    
    // Then poll every 2 seconds (faster for better UX)
    pollingIntervalRef.current = setInterval(checkSession, 2000);
  }, [getToken, handleSessionInvalidated]);

  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log('SessionMonitor: Polling stopped');
    }
  }, []);

  // WebSocket connection - SUPPLEMENTARY method (faster when available)
  const connectWebSocket = useCallback(() => {
    const token = getToken();
    if (!token) return;
    if (isInvalidatedRef.current) return;

    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
    }

    // Convert HTTP URL to WebSocket URL
    let wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    wsUrl = `${wsUrl}/api/session/ws/${token}`;

    console.log('SessionMonitor: Attempting WebSocket connection...');
    
    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('SessionMonitor: WebSocket connected');
        setIsConnected(true);
        
        // Start ping interval to keep connection alive
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'PING' }));
          }
        }, 15000);
      };

      ws.onmessage = (event) => {
        if (isInvalidatedRef.current) return;
        
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'SESSION_INVALIDATED') {
            console.log('SessionMonitor: Session invalidated detected via WebSocket');
            handleSessionInvalidated(data.message);
          } else if (data.type === 'CONNECTED') {
            console.log('SessionMonitor: WebSocket session monitoring active');
          }
        } catch (e) {
          console.error('SessionMonitor: Failed to parse WebSocket message:', e);
        }
      };

      ws.onerror = (error) => {
        console.error('SessionMonitor: WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log('SessionMonitor: WebSocket closed, code:', event.code);
        setIsConnected(false);
        
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }

        // Handle session invalidation close code
        if (event.code === 4001) {
          handleSessionInvalidated(event.reason || 'Your session has been terminated because someone logged in with your credentials on another device.');
          return;
        }

        // Try to reconnect after 5 seconds (if not invalidated)
        if (!isInvalidatedRef.current && getToken()) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, 5000);
        }
      };
    } catch (error) {
      console.error('SessionMonitor: Failed to create WebSocket:', error);
    }
  }, [getToken, handleSessionInvalidated]);

  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
  }, []);

  // Main effect - start monitoring when token exists
  useEffect(() => {
    const token = getToken();
    if (token) {
      console.log('SessionMonitor: Token found, starting monitoring');
      isInvalidatedRef.current = false;
      
      // Start polling immediately (primary method)
      startPolling();
      
      // Also try WebSocket (supplementary for faster detection)
      connectWebSocket();
    }

    return () => {
      stopPolling();
      disconnectWebSocket();
    };
  }, []);

  // Watch for token changes
  useEffect(() => {
    let lastToken = getToken();
    
    const checkToken = () => {
      const currentToken = getToken();
      
      if (currentToken !== lastToken) {
        console.log('SessionMonitor: Token changed');
        
        if (currentToken && !lastToken) {
          // Token was set - start monitoring
          console.log('SessionMonitor: New token detected, starting monitoring');
          isInvalidatedRef.current = false;
          startPolling();
          connectWebSocket();
        } else if (!currentToken && lastToken) {
          // Token was removed - stop monitoring
          console.log('SessionMonitor: Token removed, stopping monitoring');
          stopPolling();
          disconnectWebSocket();
        } else if (currentToken && lastToken && currentToken !== lastToken) {
          // Token changed - restart monitoring
          console.log('SessionMonitor: Token changed, restarting monitoring');
          isInvalidatedRef.current = false;
          stopPolling();
          disconnectWebSocket();
          setTimeout(() => {
            startPolling();
            connectWebSocket();
          }, 100);
        }
        
        lastToken = currentToken;
      }
    };
    
    // Check every 500ms for token changes
    const interval = setInterval(checkToken, 500);
    
    return () => clearInterval(interval);
  }, [getToken, startPolling, stopPolling, connectWebSocket, disconnectWebSocket]);

  // Listen for cross-tab storage changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'livestreamToken' || e.key === 'adminToken') {
        if (e.newValue) {
          // New token in another tab
          isInvalidatedRef.current = false;
          startPolling();
          connectWebSocket();
        } else {
          // Token removed in another tab
          stopPolling();
          disconnectWebSocket();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [startPolling, stopPolling, connectWebSocket, disconnectWebSocket]);

  return {
    isConnected,
    startPolling,
    stopPolling
  };
};

export default useSessionMonitor;
